import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "./api";
import WOQuickModal from "./WOQuickModal";
import WODetailsPanel from "./WODetailsPanel";

// ==============================
// Frontend image compression
// ==============================
const IMAGE_MIME = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);

function _isImageFile(file) {
  const t = (file?.type || "").toLowerCase();
  return t.startsWith("image/") || IMAGE_MIME.has(t);
}

function _sanitizeFilename(name) {
  return (name || "upload").replace(/[^\w.\-() ]+/g, "_").slice(0, 120);
}

/**
 * Compress/resize an image file in-browser.
 * - Keeps aspect ratio
 * - Resizes longest side to maxDim
 * - Outputs JPEG (best compatibility on mobile)
 */
async function compressImageFile(file, { maxDim = 1600, quality = 0.78 } = {}) {
  if (!_isImageFile(file)) return file;

  // Some phones give huge images; compressing saves bandwidth & storage
  const blob = file;

  // Decode image
  let bitmap;
  try {
    if ("createImageBitmap" in window) {
      bitmap = await createImageBitmap(blob);
    }
  } catch {
    bitmap = null;
  }

  // Fallback for browsers without createImageBitmap
  if (!bitmap) {
    const url = URL.createObjectURL(blob);
    try {
      const img = new Image();
      img.decoding = "async";
      img.src = url;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      // draw to canvas
      const w0 = img.naturalWidth || img.width;
      const h0 = img.naturalHeight || img.height;

      const scale = Math.min(1, maxDim / Math.max(w0, h0));
      const w = Math.max(1, Math.round(w0 * scale));
      const h = Math.max(1, Math.round(h0 * scale));

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);

      const outBlob = await new Promise((resolve) =>
        canvas.toBlob(
          (b) => resolve(b),
          "image/jpeg",
          quality
        )
      );

      if (!outBlob) return file;

      const base = _sanitizeFilename(file.name.replace(/\.[^.]+$/, ""));
      return new File([outBlob], `${base}.jpg`, { type: "image/jpeg" });
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  // Bitmap path
  const w0 = bitmap.width;
  const h0 = bitmap.height;

  const scale = Math.min(1, maxDim / Math.max(w0, h0));
  const w = Math.max(1, Math.round(w0 * scale));
  const h = Math.max(1, Math.round(h0 * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);

  // Release bitmap memory
  try {
    bitmap.close?.();
  } catch {}

  const outBlob = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", quality)
  );

  if (!outBlob) return file;

  const base = _sanitizeFilename(file.name.replace(/\.[^.]+$/, ""));
  return new File([outBlob], `${base}.jpg`, { type: "image/jpeg" });
}

/**
 * Process a list of selected files:
 * - images -> compressed
 * - everything else -> unchanged (pdf/doc/etc)
 */
async function prepareWODocFiles(files) {
  const out = [];
  for (const f of files) {
    if (_isImageFile(f)) {
      out.push(await compressImageFile(f, { maxDim: 1280, quality: 0.65 }));
    } else {
      out.push(f);
    }
  }
  return out;
}

// ==============================
// WO Number generator (match desktop)
// Format: WO<MMDDYYHHMMSS>
// Example: WO012926064200
// ==============================
function generateWOCode(prefix = "WO") {
  const d = new Date();

  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);

  const HH = String(d.getHours()).padStart(2, "0");
  const MM = String(d.getMinutes()).padStart(2, "0");
  const SS = String(d.getSeconds()).padStart(2, "0");

  return `${prefix}${mm}${dd}${yy}${HH}${MM}${SS}`;
}


export default function WorkOrders({ site }) {
  const [tab, setTab] = useState("create"); // create | list

  return (
    <div className="p-5">
      <h2 className="text-2xl font-semibold mb-4">Work Orders @ {site}</h2>

      <div className="flex gap-2 mb-4">
        <button
          className={`px-3 py-2 rounded ${
            tab === "create" ? "bg-blue-600 text-white" : "bg-gray-200"
          }`}
          onClick={() => setTab("create")}
          type="button"
        >
          Create WO
        </button>
        <button
          className={`px-3 py-2 rounded ${
            tab === "list" ? "bg-blue-600 text-white" : "bg-gray-200"
          }`}
          onClick={() => setTab("list")}
          type="button"
        >
          Existing WOs
        </button>
      </div>

      {tab === "create" ? <CreateWO site={site} /> : <WOList site={site} />}
    </div>
  );
}

function CreateWO({ site }) {
  const [woNumber, setWoNumber] = useState(() => generateWOCode("WO"));
  const [priority, setPriority] = useState("2");
  const [dueDate, setDueDate] = useState("");
  const [assignee, setAssignee] = useState("");
  const [assetCode, setAssetCode] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");

  const [plannedParts, setPlannedParts] = useState([]);
  const [nonInvParts, setNonInvParts] = useState([]);

  // ✅ NEW: WO Documents (queued, uploaded after save)
  const [pendingWODocs, setPendingWODocs] = useState([]); // File[]
  const [docBusy, setDocBusy] = useState(false); // ✅ NEW
  
  // NEW: Internal + External (after Non-Inventory)
  const [internalAssignee, setInternalAssignee] = useState("");

  const [externalCompany, setExternalCompany] = useState("");
  const [externalContactName, setExternalContactName] = useState("");
  const [externalEmail, setExternalEmail] = useState("");
  const [externalPhone, setExternalPhone] = useState("");

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // picker modals
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [equipOpen, setEquipOpen] = useState(false);
  const [partsOpen, setPartsOpen] = useState(false);
  const [nonInvOpen, setNonInvOpen] = useState(false);

  // NEW: internal/external modals
  const [internalOpen, setInternalOpen] = useState(false);
  const [extCompanyOpen, setExtCompanyOpen] = useState(false);
  const [extContactOpen, setExtContactOpen] = useState(false);

  const totalPlannedCost = useMemo(() => {
    return plannedParts.reduce((s, p) => s + (Number(p.total || 0) || 0), 0);
  }, [plannedParts]);

  const totalNonInvCost = useMemo(() => {
    return nonInvParts.reduce((s, p) => s + (Number(p.total || 0) || 0), 0);
  }, [nonInvParts]);

  const updateQty = (idx, qty) => {
    setPlannedParts((prev) => {
      const copy = [...prev];
      const q = Number(qty || 0) || 0;
      copy[idx] = {
        ...copy[idx],
        qty: q,
        total: q * (Number(copy[idx].unit_cost) || 0),
      };
      return copy;
    });
  };

  const removePart = (idx) => setPlannedParts((prev) => prev.filter((_, i) => i !== idx));
  const removeNonInv = (idx) => setNonInvParts((prev) => prev.filter((_, i) => i !== idx));

  // ✅ Upload queued WO documents AFTER the WO is created
  async function uploadPendingDocs(createdWoNumber) {
    if (!pendingWODocs.length) return { uploaded: 0 };
  
    const fd = new FormData();
    fd.append("site", site);
    fd.append("wo_number", createdWoNumber);
    if (assetCode) fd.append("asset_code", assetCode);
    fd.append("description", "WO Documents");
    fd.append("initial_doc", "initial");
  
    for (const f of pendingWODocs) fd.append("files", f);
  
    const r = await apiFetch("/api/wo/docs/upload", {
      method: "POST",
      body: fd,
    });
  
    const ct = r.headers.get("content-type") || "";
    const body = ct.includes("application/json")
      ? await r.json().catch(() => null)
      : await r.text();
  
    if (!r.ok) {
      const msg =
        typeof body === "string"
          ? body
          : body?.detail
          ? JSON.stringify(body.detail)
          : JSON.stringify(body);
  
      throw new Error(msg || "Upload failed.");
    }
  
    // Expected: { ok: true, uploaded: N, rows: [...] }
    return body;
  }
  
  const save = async () => {
    setErr("");
    if (!description.trim()) return setErr("WO Title is required.");
    if (!["1", "2", "3"].includes(priority))
      return setErr("Priority must be 1/2/3.");
  
    setSaving(true);
  
    try {
      const payload = {
        site,
        wo_number: woNumber,
        priority,
        due_date: dueDate || null,
        assignee: assignee || null,
        asset_code: assetCode || null,
        description,
        notes: notes || null,
        planned_parts: plannedParts,
        non_inventory_parts: nonInvParts,
  
        // internal/external
        internal_assignee: internalAssignee || null,
        external_company: externalCompany || null,
        external_contact_name: externalContactName || null,
        external_email: externalEmail || null,
        external_phone: externalPhone || null,
      };
  
      // 1) Create WO
      const r = await apiFetch("/api/wo/create", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(await r.text());
      const out = await r.json();
  
      // 2) Upload docs (must happen as part of save)
      let uploadResult = { uploaded: 0 };
      try {
        uploadResult = await uploadPendingDocs(out.wo_number);
      } catch (e) {
        // IMPORTANT: WO exists, so keep docs queued so user can retry
        throw new Error(
          `WO saved (${out.wo_number}), but document upload failed:\n${String(e.message || e)}`
        );
      }
  
      // 3) Success message
      alert(`WO Created: ${out.wo_number}\nDocuments uploaded: ${uploadResult?.uploaded ?? 0}`);
  
      // 4) Reset EVERYTHING only after BOTH steps succeed
      setWoNumber(generateWOCode("WO"));
      setPriority("2");
      setDueDate("");
      setAssignee("");
      setAssetCode("");
      setDescription("");
      setNotes("");
      setPlannedParts([]);
      setNonInvParts([]);
  
      setInternalAssignee("");
      setExternalCompany("");
      setExternalContactName("");
      setExternalEmail("");
      setExternalPhone("");
  
      setPendingWODocs([]); // ✅ clear docs only after successful upload
    } catch (e) {
      setErr(`Create failed: ${String(e.message || e)}`);
    } finally {
      setSaving(false);
    }
  };
  

  return (
    <div className="border rounded-lg p-4 bg-white shadow-sm">
      {err && <p className="text-red-600 mb-3">{err}</p>}

      {/* Top header fields */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-sm text-gray-600">WO #</label>
        
          <div className="flex gap-2">
            <input
              className="border rounded px-2 py-2 w-full font-mono"
              value={woNumber}
              readOnly
            />
            <button
              className="px-3 py-2 rounded bg-purple-300 hover:bg-purple-500 hover:text-white"
              type="button"
              onClick={() => setWoNumber(generateWOCode("WO"))}
              title="Generate new WO number"
            >
              ↻
            </button>
          </div>
        </div>


        <div>
          <label className="text-sm text-gray-600">Priority</label>
          <select
            className="border rounded px-2 py-2 w-full"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
          </select>
        </div>

        <div>
          <label className="text-sm text-gray-600">Due Date</label>
          <input
            type="date"
            className="border rounded px-2 py-2 w-full"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </div>
      </div>

      {/* Assign + Equipment rows (readonly + buttons like desktop) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <div>
          <label className="text-sm text-gray-600">Assign To</label>
          <div className="flex gap-2">
            <input
              className="border rounded px-2 py-2 w-full"
              value={assignee}
              readOnly
              placeholder="Select assignee…"
            />
            <button
              className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => setAssigneeOpen(true)}
              type="button"
            >
              ➕
            </button>
          </div>
        </div>

        <div>
          <label className="text-sm text-gray-600">Equipment (Asset Code)</label>
          <div className="flex gap-2">
            <input
              className="border rounded px-2 py-2 w-full"
              value={assetCode}
              readOnly
              placeholder="Select equipment…"
            />
            <button
              className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => setEquipOpen(true)}
              type="button"
            >
              🔍
            </button>
          </div>
        </div>
      </div>

      <div className="mt-3">
        <label className="text-sm text-gray-600">WO Title</label>
        <input
          className="border rounded px-2 py-2 w-full"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="mt-3">
        <label className="text-sm text-gray-600">Service Notes</label>
        <textarea
          className="border rounded px-2 py-2 w-full"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      {/* Inventory Planned Parts */}
      <div className="mt-4 border rounded p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="font-semibold">
            Planned Inventory Parts (planning only — no stock deducted)
          </div>
          <button
            className="px-3 py-2 rounded bg-orange-500 text-white hover:bg-orange-600"
            onClick={() => setPartsOpen(true)}
            type="button"
          >
            🔍 Search & Add Parts
          </button>
        </div>

        {plannedParts.length > 0 ? (
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr>
                  {["Part #", "Description", "UOM", "Qty", "Unit Cost", "Total", ""].map((h) => (
                    <th key={h} className="border p-2 bg-gray-100 text-left">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {plannedParts.map((p, idx) => (
                  <tr key={`${p.part_no}-${idx}`} className="hover:bg-gray-50">
                    <td className="border p-2 font-mono">{p.part_no}</td>
                    <td className="border p-2">{p.description}</td>
                    <td className="border p-2">{p.uom}</td>
                    <td className="border p-2">
                      <input
                        className="border rounded px-2 py-1 w-24"
                        value={p.qty}
                        onChange={(e) => updateQty(idx, e.target.value)}
                        inputMode="decimal"
                      />
                    </td>
                    <td className="border p-2">{Number(p.unit_cost || 0).toFixed(2)}</td>
                    <td className="border p-2">{Number(p.total || 0).toFixed(2)}</td>
                    <td className="border p-2">
                      <button
                        className="px-2 py-1 bg-gray-200 rounded"
                        onClick={() => removePart(idx)}
                        type="button"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-right mt-2 font-semibold">
              Inventory Planned Total: ${totalPlannedCost.toFixed(2)}
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-600 mt-2">No planned inventory parts added yet.</div>
        )}
      </div>

      {/* Non-Inventory Parts (planning only) */}
      <div className="mt-4 border rounded p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="font-semibold">Non-Inventory Parts (planning)</div>
          <button
            className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            onClick={() => setNonInvOpen(true)}
            type="button"
          >
            ➕ Add Non-Inventory Part
          </button>
        </div>

        {nonInvParts.length > 0 ? (
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr>
                  {["Description", "UOM", "Qty", "Unit Cost", "Total", ""].map((h) => (
                    <th key={h} className="border p-2 bg-gray-100 text-left">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {nonInvParts.map((p, idx) => (
                  <tr key={`${p.description}-${idx}`} className="hover:bg-gray-50">
                    <td className="border p-2">{p.description}</td>
                    <td className="border p-2">{p.uom}</td>
                    <td className="border p-2">{String(p.qty)}</td>
                    <td className="border p-2">{Number(p.unit_cost || 0).toFixed(2)}</td>
                    <td className="border p-2">{Number(p.total || 0).toFixed(2)}</td>
                    <td className="border p-2">
                      <button
                        className="px-2 py-1 bg-gray-200 rounded"
                        onClick={() => removeNonInv(idx)}
                        type="button"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="text-right mt-2 font-semibold">
              Non-Inventory Planned Total: ${totalNonInvCost.toFixed(2)}
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-600 mt-2">No non-inventory parts added yet.</div>
        )}
      </div>

      {/* =========================================================
          NEW: INTERNAL + EXTERNAL (added AFTER Non-Inventory block)
          ========================================================= */}

      {/* Internal (use Assign To picker style) */}
      <div className="mt-4 border rounded p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="font-semibold">Internal (Labor / Assigned Tech)</div>
          <button
            className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            onClick={() => setInternalOpen(true)}
            type="button"
          >
            ➕ Select Internal
          </button>
        </div>

        <div className="mt-3">
          <label className="text-sm text-gray-600">Internal Assignee</label>
          <input
            className="border rounded px-2 py-2 w-full"
            value={internalAssignee}
            readOnly
            placeholder="Select internal assignee…"
          />
        </div>

        {internalAssignee ? (
          <div className="mt-2">
            <button
              className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300"
              type="button"
              onClick={() => setInternalAssignee("")}
            >
              Clear
            </button>
          </div>
        ) : null}
      </div>

      {/* External (company + contact pickers) */}
      <div className="mt-4 border rounded p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="font-semibold">External Contractor</div>
          <div className="flex gap-2">
            <button
              className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => setExtCompanyOpen(true)}
              type="button"
            >
              🏢 Pick Company
            </button>
            <button
              className={`px-3 py-2 rounded ${
                externalCompany
                  ? "bg-orange-500 text-white hover:bg-orange-600"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              }`}
              onClick={() => {
                if (!externalCompany) return;
                setExtContactOpen(true);
              }}
              type="button"
              disabled={!externalCompany}
              title={!externalCompany ? "Pick a company first" : "Pick a contact"}
            >
              👤 Pick Contact
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-sm text-gray-600">Company</label>
            <input
              className="border rounded px-2 py-2 w-full"
              value={externalCompany}
              readOnly
              placeholder="Select company…"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">Contact</label>
            <input
              className="border rounded px-2 py-2 w-full"
              value={externalContactName}
              readOnly
              placeholder="Select contact…"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">Email</label>
            <input
              className="border rounded px-2 py-2 w-full"
              value={externalEmail}
              readOnly
              placeholder="—"
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">Phone</label>
            <input
              className="border rounded px-2 py-2 w-full"
              value={externalPhone}
              readOnly
              placeholder="—"
            />
          </div>
        </div>

        {(externalCompany || externalContactName || externalEmail || externalPhone) ? (
          <div className="mt-3">
            <button
              className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300"
              type="button"
              onClick={() => {
                setExternalCompany("");
                setExternalContactName("");
                setExternalEmail("");
                setExternalPhone("");
              }}
            >
              Clear External
            </button>
          </div>
        ) : null}
      </div>

      {/* ✅ NEW: WO Documents (added AFTER internal + external section) */}
      <div className="mt-4 border rounded p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold">WO Documents</div>
          {docBusy && (
            <div className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">
              Processing…
            </div>
          )}
        </div>
      
        <div className="flex flex-wrap gap-2">
          <label className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 cursor-pointer">
            Upload File(s)
            <input
              type="file"
              multiple
              className="hidden"
              onChange={async (e) => {
                const raw = Array.from(e.target.files || []);
                e.target.value = "";
                if (!raw.length) return;
      
                setDocBusy(true);
                try {
                  // compress images only
                  const processed = await prepareWODocFiles(raw, { maxDim: 1280, quality: 0.65 });
                  setPendingWODocs((prev) => [...prev, ...processed]);
                } finally {
                  setDocBusy(false);
                }
              }}
            />
          </label>
      
          <label className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 cursor-pointer">
            Take Picture
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={async (e) => {
                const raw = Array.from(e.target.files || []);
                e.target.value = "";
                if (!raw.length) return;
      
                setDocBusy(true);
                try {
                  // force compress (these are images)
                  const processed = await prepareWODocFiles(raw, { maxDim: 1200, quality: 0.60 });
                  setPendingWODocs((prev) => [...prev, ...processed]);
                } finally {
                  setDocBusy(false);
                }
              }}
            />
          </label>
      
          <button
            className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            type="button"
            onClick={() => setPendingWODocs([])}
            disabled={pendingWODocs.length === 0 || docBusy}
            title={docBusy ? "Processing documents…" : ""}
          >
            Clear
          </button>
        </div>
      
        {pendingWODocs.length > 0 && (
          <div className="mt-2 text-sm text-gray-700">
            <div className="font-semibold mb-1">Queued:</div>
            <ul className="list-disc pl-5">
              {pendingWODocs.map((f, i) => (
                <li key={`${f.name}-${i}`}>{f.name}</li>
              ))}
            </ul>
            <div className="text-xs text-gray-500 mt-1">
              These will upload automatically when you save the Work Order.
            </div>
          </div>
        )}
      </div>

      <div className="mt-4">
        <button
          className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
          disabled={saving || docBusy}
          onClick={save}
          title={docBusy ? "Processing documents…" : ""}
        >
          {saving ? "Saving…" : "Save New Work Order"}
        </button>
      </div>

      {/* Modals */}
      <AssigneePickerModal
        open={assigneeOpen}
        onClose={() => setAssigneeOpen(false)}
        site={site}
        onPick={(u) => setAssignee(u)}
      />

      <EquipmentPickerModal
        open={equipOpen}
        onClose={() => setEquipOpen(false)}
        site={site}
        onPick={(asset_code) => setAssetCode(asset_code)}
      />

      <PartsPickerModal
        open={partsOpen}
        onClose={() => setPartsOpen(false)}
        site={site}
        onAdd={(row) => {
          const pn = row["Part Number"];
          const tt = String(row["Transaction Type"] || "").toLowerCase();
          const uom = tt === "bulk" ? (row["Received UOM"] || "") : (row["UOM Data"] || "");
          const unitCost = Number(row["Cost Per Item"] || 0) || 0;
          const qty = 1;

          setPlannedParts((prev) => [
            ...prev,
            {
              part_no: pn,
              description: row.Description || "",
              uom,
              qty,
              unit_cost: unitCost,
              total: qty * unitCost,
            },
          ]);
        }}
      />

      <NonInvPartsModal
        open={nonInvOpen}
        onClose={() => setNonInvOpen(false)}
        onAdd={(rec) => setNonInvParts((prev) => [...prev, rec])}
      />

      {/* NEW: internal/external modals */}
      <AssigneePickerModal
        open={internalOpen}
        onClose={() => setInternalOpen(false)}
        site={site}
        onPick={(u) => setInternalAssignee(u)}
        titleOverride="Select Internal Assignee"
      />

      <ExternalCompanyPickerModal
        open={extCompanyOpen}
        onClose={() => setExtCompanyOpen(false)}
        site={site}
        onPick={(rec) => {
          // picking a company can also set a "representative" contact (from the endpoint)
          setExternalCompany(rec.company || "");
          setExternalContactName(rec.contact_name || "");
          setExternalEmail(rec.email || "");
          setExternalPhone(rec.phone || "");
        }}
      />

      <ExternalContactPickerModal
        open={extContactOpen}
        onClose={() => setExtContactOpen(false)}
        site={site}
        company={externalCompany}
        onPick={(rec) => {
          setExternalContactName(rec.contact_name || "");
          setExternalEmail(rec.email || "");
          setExternalPhone(rec.phone || "");
        }}
      />
    </div>
  );
}

function AssigneePickerModal({ open, onClose, site, onPick, titleOverride }) {
  const [company, setCompany] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [status, setStatus] = useState("Active"); // Active | Deactive | All

  const [companyOptions, setCompanyOptions] = useState([]);

  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  // Load dropdown options (companies)
  useEffect(() => {
    if (!open) return;
    let alive = true;

    async function loadOptions() {
      try {
        const qs = new URLSearchParams({ site });
        const r = await apiFetch(`/api/pickers/users/options?${qs}`);
        const data = r.ok ? await r.json() : {};
        if (alive) setCompanyOptions(data?.companies || []);
      } catch {
        if (alive) setCompanyOptions([]);
      }
    }

    loadOptions();
    return () => {
      alive = false;
    };
  }, [open, site]);

  // Load filtered results
  useEffect(() => {
    if (!open) return;
    let alive = true;

    async function load() {
      setErr("");
      try {
        const qs = new URLSearchParams({
          site,
          company,
          contact,
          email,
          username,
          status,
        });
        const r = await apiFetch(`/api/pickers/users?${qs}`);
        const data = r.ok ? await r.json() : [];
        if (alive) setRows(data || []);
      } catch {
        if (alive) setErr("Failed to load users");
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [open, site, company, contact, email, username, status]);

  if (!open) return null;

  return (
    <Modal title={titleOverride || "Select Assignee"} onClose={onClose}>
      {err && <p className="text-red-600 mb-2">{err}</p>}

      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
        <select
          className="border rounded px-2 py-2"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        >
          <option value="">All Companies</option>
          {companyOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <input
          className="border rounded px-2 py-2"
          placeholder="Contact"
          value={contact}
          onChange={(e) => setContact(e.target.value)}
        />
        <input
          className="border rounded px-2 py-2"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          className="border rounded px-2 py-2"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <select
          className="border rounded px-2 py-2"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option>Active</option>
          <option>Deactive</option>
          <option>All</option>
        </select>
      </div>

      <div className="max-h-96 overflow-auto border rounded">
        {rows.map((r) => (
          <button
            key={r.id}
            className="w-full text-left px-3 py-2 border-b hover:bg-gray-50"
            onClick={() => {
              onPick(r.username || `${r.company} – ${r.contact_name}`);
              onClose();
            }}
            type="button"
          >
            <div className="font-semibold">{r.username || "(no username)"}</div>
            <div className="text-xs text-gray-600">
              {r.company} — {r.contact_name}
            </div>
            <div className="text-xs text-gray-600">{r.email}</div>
          </button>
        ))}
        {rows.length === 0 && <div className="p-3 text-sm text-gray-600">No users found.</div>}
      </div>
    </Modal>
  );
}

function ExternalCompanyPickerModal({ open, onClose, site, onPick }) {
  const [company, setCompany] = useState("");
  const [companyOptions, setCompanyOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  // options
  useEffect(() => {
    if (!open) return;
    let alive = true;

    async function loadOptions() {
      try {
        const qs = new URLSearchParams({ site });
        const r = await apiFetch(`/api/pickers/external_companies/options?${qs}`);
        const data = r.ok ? await r.json() : {};
        if (alive) setCompanyOptions(data?.companies || []);
      } catch {
        if (alive) setCompanyOptions([]);
      }
    }

    loadOptions();
    return () => {
      alive = false;
    };
  }, [open, site]);

  // rows
  useEffect(() => {
    if (!open) return;
    let alive = true;

    async function load() {
      setErr("");
      try {
        const qs = new URLSearchParams({ site, company, limit: "300" });
        const r = await apiFetch(`/api/pickers/external_companies?${qs}`);
        const data = r.ok ? await r.json() : [];
        if (alive) setRows(data || []);
      } catch {
        if (alive) setErr("Failed to load companies");
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [open, site, company]);

  useEffect(() => {
    if (!open) return;
    setCompany("");
  }, [open]);

  if (!open) return null;

  return (
    <Modal title="Select External Company" onClose={onClose}>
      {err && <p className="text-red-600 mb-2">{err}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        <select
          className="border rounded px-2 py-2"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        >
          <option value="">All Companies</option>
          {companyOptions.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <input
          className="border rounded px-2 py-2"
          placeholder="Search company…"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />
      </div>

      <div className="max-h-96 overflow-auto border rounded">
        {rows.map((r, idx) => (
          <button
            key={`${r.company}-${idx}`}
            className="w-full text-left px-3 py-2 border-b hover:bg-gray-50"
            onClick={() => {
              onPick(r);
              onClose();
            }}
            type="button"
          >
            <div className="font-semibold">{r.company}</div>
            <div className="text-xs text-gray-600">
              {r.contact_name || "—"} • {r.email || "—"} • {r.phone || "—"}
            </div>
          </button>
        ))}
        {rows.length === 0 && <div className="p-3 text-sm text-gray-600">No companies found.</div>}
      </div>
    </Modal>
  );
}

function ExternalContactPickerModal({ open, onClose, site, company, onPick }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    let alive = true;

    async function load() {
      setErr("");
      try {
        const qs = new URLSearchParams({
          site,
          company,
          name,
          email,
          phone,
          limit: "300",
        });
        const r = await apiFetch(`/api/pickers/external_contacts?${qs}`);
        const data = r.ok ? await r.json() : [];
        if (alive) setRows(data || []);
      } catch {
        if (alive) setErr("Failed to load contacts");
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [open, site, company, name, email, phone]);

  useEffect(() => {
    if (!open) return;
    setName("");
    setEmail("");
    setPhone("");
  }, [open]);

  if (!open) return null;

  return (
    <Modal title={`Select Contact (${company || "Company"})`} onClose={onClose}>
      {err && <p className="text-red-600 mb-2">{err}</p>}

      {!company ? (
        <div className="p-3 text-sm text-gray-600">Pick a company first.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
            <input
              className="border rounded px-2 py-2"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input
              className="border rounded px-2 py-2"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              className="border rounded px-2 py-2"
              placeholder="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div className="max-h-96 overflow-auto border rounded">
            {rows.map((r, idx) => (
              <button
                key={`${r.company}-${r.contact_name}-${idx}`}
                className="w-full text-left px-3 py-2 border-b hover:bg-gray-50"
                onClick={() => {
                  onPick(r);
                  onClose();
                }}
                type="button"
              >
                <div className="font-semibold">{r.contact_name || "—"}</div>
                <div className="text-xs text-gray-600">{r.email || "—"}</div>
                <div className="text-xs text-gray-600">{r.phone || "—"}</div>
              </button>
            ))}
            {rows.length === 0 && <div className="p-3 text-sm text-gray-600">No contacts found.</div>}
          </div>
        </>
      )}
    </Modal>
  );
}

function EquipmentPickerModal({ open, onClose, site, onPick }) {
  const [assetCode, setAssetCode] = useState("");
  const [desc, setDesc] = useState("");
  const [mfg, setMfg] = useState("");
  const [model, setModel] = useState("");
  const [cat, setCat] = useState("");
  const [job, setJob] = useState("");

  const [opt, setOpt] = useState({
    descriptions: [],
    manufacturers: [],
    models: [],
    categories: [],
    job_codes: [],
  });

  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  // Load dropdown options once when modal opens
  useEffect(() => {
    if (!open) return;
    let alive = true;

    async function loadOptions() {
      try {
        const qs = new URLSearchParams({ site });
        const r = await apiFetch(`/api/pickers/equipment/options?${qs}`);
        const data = r.ok ? await r.json() : null;
        if (alive && data) {
          setOpt({
            descriptions: data.descriptions || [],
            manufacturers: data.manufacturers || [],
            models: data.models || [],
            categories: data.categories || [],
            job_codes: data.job_codes || [],
          });
        }
      } catch {
        if (alive) {
          setOpt({
            descriptions: [],
            manufacturers: [],
            models: [],
            categories: [],
            job_codes: [],
          });
        }
      }
    }

    loadOptions();
    return () => {
      alive = false;
    };
  }, [open, site]);

  // Load filtered results
  useEffect(() => {
    if (!open) return;
    let alive = true;

    async function load() {
      setErr("");
      try {
        const qs = new URLSearchParams({
          site,
          asset_code: assetCode,
          description: desc,
          manufacturer: mfg,
          model_number: model,
          category: cat,
          job_code: job,
        });
        const r = await apiFetch(`/api/pickers/equipment?${qs}`);
        const data = r.ok ? await r.json() : [];
        if (alive) setRows(data || []);
      } catch {
        if (alive) setErr("Failed to load equipment");
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [open, site, assetCode, desc, mfg, model, cat, job]);

  if (!open) return null;

  return (
    <Modal title="Select Equipment" onClose={onClose}>
      {err && <p className="text-red-600 mb-2">{err}</p>}

      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-3">
        {/* Asset code stays a text filter */}
        <input
          className="border rounded px-2 py-2"
          placeholder="Asset Code"
          value={assetCode}
          onChange={(e) => setAssetCode(e.target.value)}
        />

        <select className="border rounded px-2 py-2" value={desc} onChange={(e) => setDesc(e.target.value)}>
          <option value="">All Descriptions</option>
          {opt.descriptions.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>

        <select className="border rounded px-2 py-2" value={mfg} onChange={(e) => setMfg(e.target.value)}>
          <option value="">All Manufacturers</option>
          {opt.manufacturers.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>

        <select className="border rounded px-2 py-2" value={model} onChange={(e) => setModel(e.target.value)}>
          <option value="">All Models</option>
          {opt.models.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>

        <select className="border rounded px-2 py-2" value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">All Categories</option>
          {opt.categories.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>

        <select className="border rounded px-2 py-2" value={job} onChange={(e) => setJob(e.target.value)}>
          <option value="">All Job Codes</option>
          {opt.job_codes.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </div>

      <div className="max-h-96 overflow-auto border rounded">
        {rows.map((r) => (
          <button
            key={r.asset_code}
            className="w-full text-left px-3 py-2 border-b hover:bg-gray-50"
            onClick={() => {
              onPick(r.asset_code);
              onClose();
            }}
            type="button"
          >
            <div className="font-mono">{r.asset_code}</div>
            <div className="text-sm text-gray-700">{r.description || ""}</div>
            <div className="text-xs text-gray-600">
              {r.manufacturer || ""} {r.model_number || ""} • {r.category || ""}
            </div>
          </button>
        ))}
        {rows.length === 0 && <div className="p-3 text-sm text-gray-600">No equipment found.</div>}
      </div>
    </Modal>
  );
}

function PartsPickerModal({ open, onClose, site, onAdd }) {
  const [partNumber, setPartNumber] = useState("");
  const [desc, setDesc] = useState("");

  // dropdown filters
  const [cat, setCat] = useState("");
  const [mfg, setMfg] = useState("");
  const [loc, setLoc] = useState("");
  const [dept, setDept] = useState("");
  const [pj, setPj] = useState("");
  const [equip, setEquip] = useState("");
  const [asset, setAsset] = useState("");

  const [opt, setOpt] = useState({
    categories: [],
    manufacturers: [],
    locations: [],
    departments: [],
    project_jobs: [],
    equipment: [],
    asset_codes: [],
  });

  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  // ✅ NEW: load control
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ NEW: pagination
  const PAGE_SIZE = 75; // tune this (50/75/100)
  const [page, setPage] = useState(0);

  // Load dropdown options once when modal opens
  useEffect(() => {
    if (!open) return;
    let alive = true;

    async function loadOptions() {
      try {
        const qs = new URLSearchParams({ site });
        const r = await apiFetch(`/api/pickers/parts/options?${qs}`);
        const data = r.ok ? await r.json() : null;
        if (!alive) return;

        setOpt({
          categories: data?.categories || [],
          manufacturers: data?.manufacturers || [],
          locations: data?.locations || [],
          departments: data?.departments || [],
          project_jobs: data?.project_jobs || [],
          equipment: data?.equipment || [],
          asset_codes: data?.asset_codes || [],
        });
      } catch {
        if (!alive) return;
        setOpt({
          categories: [],
          manufacturers: [],
          locations: [],
          departments: [],
          project_jobs: [],
          equipment: [],
          asset_codes: [],
        });
      }
    }

    // reset state on open (important on mobile)
    setRows([]);
    setErr("");
    setLoadedOnce(false);
    setLoading(false);
    setPage(0);

    loadOptions();
    return () => {
      alive = false;
    };
  }, [open, site]);

  // ✅ NEW: manual load function (Load/Refresh button)
  const loadRows = async (pageToLoad = page) => {
    setLoading(true);
    setErr("");

    try {
      const qs = new URLSearchParams({
        site,
        part_number: partNumber,
        description: desc,
        category: cat,
        manufacturer: mfg,
        location: loc,
        department: dept,
        project_job: pj,
        equipment: equip,
        asset_code: asset,

        // backend currently supports "limit" only.
        // We'll emulate paging client-side by requesting (page+1)*PAGE_SIZE and slicing.
        limit: String((pageToLoad + 1) * PAGE_SIZE),
      });

      const r = await apiFetch(`/api/pickers/parts?${qs}`);
      if (!r.ok) throw new Error(await r.text());

      const data = (await r.json()) || [];
      // show only current page slice
      const start = pageToLoad * PAGE_SIZE;
      const chunk = data.slice(start, start + PAGE_SIZE);

      setRows(chunk);
      setLoadedOnce(true);
    } catch (e) {
      setErr(`Failed to load parts: ${String(e.message || e)}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const clearFilters = () => {
    setPartNumber("");
    setDesc("");
    setCat("");
    setMfg("");
    setLoc("");
    setDept("");
    setPj("");
    setEquip("");
    setAsset("");
    setPage(0);
    setRows([]);
    setLoadedOnce(false);
    setErr("");
  };

  const goPrev = async () => {
    if (page <= 0) return;
    const nextPage = page - 1;
    setPage(nextPage);
    await loadRows(nextPage);
  };

  const goNext = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    await loadRows(nextPage);
  };

  return (
    <Modal title="Search & Add Parts" onClose={onClose}>
      {err && <p className="text-red-600 mb-2">{err}</p>}

      {/* Row 1: text filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
        <input
          className="border rounded px-2 py-2"
          placeholder="Part Number"
          value={partNumber}
          onChange={(e) => setPartNumber(e.target.value)}
        />
        <input
          className="border rounded px-2 py-2 md:col-span-3"
          placeholder="Description"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
        />
      </div>

      {/* Row 2: dropdown filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
        <select className="border rounded px-2 py-2" value={dept} onChange={(e) => setDept(e.target.value)}>
          <option value="">All Departments</option>
          {opt.departments.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>

        <select className="border rounded px-2 py-2" value={pj} onChange={(e) => setPj(e.target.value)}>
          <option value="">All Project Jobs</option>
          {opt.project_jobs.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>

        <select className="border rounded px-2 py-2" value={equip} onChange={(e) => setEquip(e.target.value)}>
          <option value="">All Equipment</option>
          {opt.equipment.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>

        <select className="border rounded px-2 py-2" value={loc} onChange={(e) => setLoc(e.target.value)}>
          <option value="">All Locations</option>
          {opt.locations.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>

        <select className="border rounded px-2 py-2" value={asset} onChange={(e) => setAsset(e.target.value)}>
          <option value="">All Asset Codes</option>
          {opt.asset_codes.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>

        <select className="border rounded px-2 py-2" value={cat} onChange={(e) => setCat(e.target.value)}>
          <option value="">All Categories</option>
          {opt.categories.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>

        <select className="border rounded px-2 py-2" value={mfg} onChange={(e) => setMfg(e.target.value)}>
          <option value="">All Manufacturers</option>
          {opt.manufacturers.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>

        <button
          className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300"
          type="button"
          onClick={clearFilters}
        >
          Clear Filters
        </button>
      </div>

      {/* ✅ NEW: Load/Refresh + Paging row */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button
          className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          type="button"
          disabled={loading}
          onClick={() => loadRows(0).then(() => setPage(0))}
          title="Load or refresh results"
        >
          {loading ? "Loading…" : loadedOnce ? "Refresh Parts" : "Load Parts"}
        </button>

        <div className="text-sm text-gray-600">
          Page: <span className="font-semibold">{page + 1}</span> • Showing {rows.length} (page size {PAGE_SIZE})
        </div>

        <div className="ml-auto flex gap-2">
          <button
            className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            type="button"
            disabled={loading || page === 0 || !loadedOnce}
            onClick={goPrev}
          >
            ◀ Prev
          </button>

          <button
            className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            type="button"
            disabled={loading || !loadedOnce || rows.length < PAGE_SIZE}
            onClick={goNext}
            title={rows.length < PAGE_SIZE ? "No more results loaded" : "Next page"}
          >
            Next ▶
          </button>
        </div>
      </div>

      <div className="max-h-96 overflow-auto border rounded">
        {!loadedOnce ? (
          <div className="p-3 text-sm text-gray-600">
            Click <b>Load Parts</b> to fetch results. Use filters first to speed it up.
          </div>
        ) : rows.map((r) => (
          <button
            key={r["Part Number"]}
            className="w-full text-left px-3 py-2 border-b hover:bg-gray-50"
            onClick={() => {
              onAdd(r);
              onClose();
            }}
            type="button"
          >
            <div className="font-mono">{r["Part Number"]}</div>
            <div className="text-sm text-gray-700">{r.Description || ""}</div>
            <div className="text-xs text-gray-600">
              On Hand: {r["Total Quantity"]} • Loc: {r["Location Display"] || r.Location || "-"} • UOM:{" "}
              {r["UOM Display"] || "-"} • Cat: {r.Category || "-"}
            </div>
          </button>
        ))}

        {loadedOnce && rows.length === 0 && (
          <div className="p-3 text-sm text-gray-600">No parts found.</div>
        )}
      </div>

      <div className="text-xs text-gray-500 mt-2">
        Tip: Set filters, then click <b>Load Parts</b>. Refresh uses the same button.
      </div>
    </Modal>
  );
}


function NonInvPartsModal({ open, onClose, onAdd }) {
  const [description, setDescription] = useState("");
  const [uom, setUom] = useState("");
  const [qty, setQty] = useState("1");
  const [unitCost, setUnitCost] = useState("0");

  const total = (Number(qty || 0) || 0) * (Number(unitCost || 0) || 0);

  useEffect(() => {
    if (!open) return;
    setDescription("");
    setUom("");
    setQty("1");
    setUnitCost("0");
  }, [open]);

  if (!open) return null;

  return (
    <Modal title="Add Non-Inventory Part" onClose={onClose}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="text-sm text-gray-600">Description</label>
          <input
            className="border rounded px-2 py-2 w-full"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div>
          <label className="text-sm text-gray-600">UOM</label>
          <input className="border rounded px-2 py-2 w-full" value={uom} onChange={(e) => setUom(e.target.value)} />
        </div>

        <div>
          <label className="text-sm text-gray-600">Qty</label>
          <input
            className="border rounded px-2 py-2 w-full"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            inputMode="decimal"
          />
        </div>

        <div>
          <label className="text-sm text-gray-600">Unit Cost</label>
          <input
            className="border rounded px-2 py-2 w-full"
            value={unitCost}
            onChange={(e) => setUnitCost(e.target.value)}
            inputMode="decimal"
          />
        </div>

        <div>
          <label className="text-sm text-gray-600">Total</label>
          <div className="border rounded px-2 py-2 w-full bg-gray-50">{total.toFixed(2)}</div>
        </div>
      </div>

      <div className="mt-4">
        <button
          className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
          type="button"
          onClick={() => {
            if (!description.trim()) return;
            const q = Number(qty || 0) || 0;
            const uc = Number(unitCost || 0) || 0;
            onAdd({
              description: description.trim(),
              uom: uom.trim(),
              qty: q,
              unit_cost: uc,
              total: q * uc,
            });
            onClose();
          }}
        >
          Add
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-xl w-[min(900px,95vw)] max-h-[90vh] overflow-hidden shadow-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold">{title}</div>
          <button className="px-3 py-1 rounded bg-gray-200" onClick={onClose} type="button">
            Close
          </button>
        </div>
        <div className="p-4 overflow-auto max-h-[80vh]">{children}</div>
      </div>
    </div>
  );
}


function WOList({ site }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");

  // chooser modal
  const [quickOpen, setQuickOpen] = useState(false);
  const [selectedWO, setSelectedWO] = useState(null);

  // details panel
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedWoNumber, setSelectedWoNumber] = useState("");

  const load = async () => {
    setErr("");
    setQuickOpen(false);
    setSelectedWO(null);

    try {
      const qs = new URLSearchParams({ site, status: "Open" });
      const r = await apiFetch(`/api/wo/list?${qs}`);
      if (!r.ok) throw new Error(await r.text());
      setRows(await r.json());
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  useEffect(() => {
    load();
  }, [site]);

  const openQuick = (row) => {
    setSelectedWO(row);
    setQuickOpen(true);
  };

  // ✅ called by WOQuickModal
  const handleOpenDetails = (woNumber) => {
    setQuickOpen(false);
    setSelectedWoNumber(woNumber);
    setDetailsOpen(true);
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="border rounded-lg p-4 bg-white shadow-sm">
        <div className="flex justify-between items-center mb-3">
          <div className="text-lg font-semibold">Open WOs</div>
          <button
            className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300"
            onClick={load}
            type="button"
          >
            Refresh
          </button>
        </div>

        {err && <p className="text-red-600 mb-2">{err}</p>}

        <div className="max-h-[520px] overflow-auto border rounded">
          {rows.map((r) => (
            <button
              key={r.wo_number}
              className="w-full text-left px-3 py-2 border-b hover:bg-gray-50"
              onClick={() => openQuick(r)}
              type="button"
            >
              <div className="font-mono">{r.wo_number}</div>
              <div className="text-sm text-gray-700">{r.description}</div>
              <div className="text-xs text-gray-500">
                Priority {r.priority} • Due {r.due_date || "-"} •{" "}
                {r.asset_code || "Non-asset"}
              </div>
            </button>
          ))}

          {rows.length === 0 && (
            <div className="p-3 text-sm text-gray-600">No open WOs.</div>
          )}
        </div>
      </div>

      {/* ✅ Quick chooser */}
      <WOQuickModal
        open={quickOpen}
        site={site}
        woSummary={selectedWO}
        onClose={() => setQuickOpen(false)}
        onOpenDetails={handleOpenDetails}
      />

      {/* ✅ Details panel (Back returns to chooser) */}
      <WODetailsPanel
        open={detailsOpen}
        site={site}
        woNumber={selectedWoNumber}
        onClose={() => setDetailsOpen(false)}
        onBack={() => {
          setDetailsOpen(false);
          setQuickOpen(true);
        }}
      />
    </div>
  );
}
